// The one place the client decides whether a call was missed (CRM-1235). Every
// surface — the inbox thread card, the conversation list, the lead activity
// log, the Calls screen — imports from here so they can never disagree about
// the same call.

/**
 * Terminal statuses of `BoomCRM::Call`'s state machine. Anything else is still
 * in flight (authorized / queued / ringing / in_progress).
 */
const TERMINAL_CALL_STATUSES: ReadonlySet<string> = new Set([
  "completed",
  "no_answer",
  "busy",
  "failed",
  "canceled",
  "expired_before_dial"
]);

interface MissedCallInput {
  call_status: string;
  // Computed by boompay-api (`BoomCRM::Call#missed?`) and carried through the
  // communication-service projection. Null/absent on rows written before that
  // shipped — those fall back to the derivation below.
  missed?: boolean | null;
  answered_at?: string | null;
}

/**
 * Whether an inbound call was never picked up.
 *
 * The backend flag wins whenever it is present: it is the canonical predicate
 * and it also knows about `answered_by_partner_user_id`, which the projection
 * does not carry. The fallback deliberately keys on `answered_at` rather than
 * the status alone — boompay-api stamps `answered_at` unconditionally on
 * :mark_answered but drops the agent attribution when the child leg's `To`
 * doesn't match `client:agent-<id>`, so a genuinely answered call can arrive
 * with no answerer recorded.
 */
export const isMissedCall = (
  voiceDetails: MissedCallInput | null | undefined,
  isInbound: boolean
): boolean => {
  if (!voiceDetails) return false;
  if (typeof voiceDetails.missed === "boolean") return voiceDetails.missed;

  const status = voiceDetails.call_status;
  return (
    isInbound &&
    TERMINAL_CALL_STATUSES.has(status) &&
    status !== "completed" &&
    !voiceDetails.answered_at
  );
};

/**
 * "M:SS" — the shape the call card has always rendered durations in.
 */
export const formatCallDuration = (
  seconds: number | null | undefined
): string | null => {
  if (seconds == null || seconds < 0) return null;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

// A real inbound ring is bounded by BoomCRM::Call::StartInbound's 30s dial
// timeout. A row terminalized by the stuck-call reaper instead carries the four
// hours it sat wedged, which is not ring time at all — and the projection
// carries no `completion_source` to identify it by. An hour is an order
// of magnitude past any genuine ring, so anything beyond it is discarded rather
// than rendered as "Rang 240:00".
const MAX_PLAUSIBLE_RING_SECONDS = 60 * 60;

/**
 * How long the phone rang. `duration_seconds` is Twilio's CallDuration, which
 * is 0 for a call nobody answered, so ring time has to come from the
 * timestamps: boompay-api stamps `started_at` when the inbound row is created
 * at ring time and `ended_at` on any terminal event.
 */
export const ringSeconds = (voiceDetails: {
  started_at?: string | null;
  ended_at?: string | null;
}): number | null => {
  const { started_at: startedAt, ended_at: endedAt } = voiceDetails;
  if (!startedAt || !endedAt) return null;

  const start = Date.parse(startedAt);
  const end = Date.parse(endedAt);
  if (Number.isNaN(start) || Number.isNaN(end)) return null;

  const elapsed = Math.floor((end - start) / 1000);
  if (elapsed < 0 || elapsed > MAX_PLAUSIBLE_RING_SECONDS) return null;
  return elapsed;
};
